// PayU checkout launcher. Tries PayU's in-page Bolt (Checkout Plus) popup — the
// Razorpay-style overlay — and if it can't render for ANY reason (SDK blocked by
// the browser, not enabled for the merchant, launch throws, overlay never mounts),
// it seamlessly falls back to the PayU HOSTED REDIRECT. Both are PayU with the
// same signed params, so checkout always proceeds and can never hang.
//
// Docs: https://docs.payu.in/docs/integrate-checkout-plus

const BOLT_SRC = {
  live: "https://jssdk.payu.in/bolt/bolt.min.js",
  test: "https://jssdk-uat.payu.in/bolt/bolt.min.js",
};

let loadPromise: Promise<void> | null = null;

// Load the Bolt SDK with a hard timeout so a blocked/slow script can't stall us.
function loadBolt(mode: "test" | "live"): Promise<void> {
  if ((window as any).bolt?.launch) return Promise.resolve();
  if (loadPromise) return loadPromise;
  const src = BOLT_SRC[mode] || BOLT_SRC.test;
  loadPromise = new Promise((resolve, reject) => {
    const done = (ok: boolean) => (ok ? resolve() : (loadPromise = null, reject(new Error("bolt_load_failed"))));
    const t = setTimeout(() => done(false), 4000);
    const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
    if (existing) {
      if ((window as any).bolt?.launch) { clearTimeout(t); return resolve(); }
      existing.addEventListener("load", () => { clearTimeout(t); done(true); });
      existing.addEventListener("error", () => { clearTimeout(t); done(false); });
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => { clearTimeout(t); done(true); };
    s.onerror = () => { clearTimeout(t); done(false); };
    document.body.appendChild(s);
  });
  return loadPromise;
}

// The Bolt overlay mounts an iframe (#boltFrame). Poll for it so we can tell
// whether the popup actually rendered vs silently no-op'd. When found, force it
// (and its positioned ancestors) to the very top so it can never render BEHIND
// an app modal/bottom-sheet that has its own high z-index.
function elevate(el: Element | null) {
  let node: HTMLElement | null = el as HTMLElement | null;
  for (let i = 0; node && i < 6; i++) {
    node.style.zIndex = "2147483647";
    if (getComputedStyle(node).position === "static") node.style.position = "relative";
    node = node.parentElement;
  }
}
function waitForBoltFrame(timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const frame = document.querySelector('#boltFrame, iframe[id*="bolt" i], iframe[src*="payu" i]');
      if (frame) { elevate(frame); return resolve(true); }
      if (Date.now() - start >= timeoutMs) return resolve(false);
      setTimeout(tick, 150);
    };
    tick();
  });
}

// Keys in the server response's `params` that are OURS (frontend routing hints),
// not PayU request fields. They must never reach PayU: `currency` is a reserved
// field name on PayU's newer API, and posting it alongside `transactionCurrency`
// is the one thing our request does that the docs
// (docs.payu.in — hosted checkout international payments) do not.
const NON_PAYU_KEYS = new Set(["currency"]);

function payuOnlyParams(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (!NON_PAYU_KEYS.has(k)) out[k] = v;
  });
  return out;
}

function submitRedirectForm(payuUrl: string, params: Record<string, unknown>) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = payuUrl;
  Object.entries(payuOnlyParams(params)).forEach(([k, v]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = String(v);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

export type PayuResp = { payu_url: string; params: Record<string, unknown>; mode?: string };

// Start PayU checkout. `onPopupResult` fires ONLY when the in-page popup is used
// (success → you navigate; failure → re-enable the button). If the popup can't
// render, we redirect instead and this callback never fires (the page navigates
// away and settlement/redirect happens server-side via surl/webhook).
export async function startPayuCheckout(
  resp: PayuResp,
  onPopupResult: (outcome: "success" | "failure") => void,
): Promise<void> {
  const params = resp.params;
  const mode = (resp.mode === "live" ? "live" : "test") as "test" | "live";
  let settled = false;

  // International (non-INR) checkout doesn't render inside the Bolt iframe —
  // PayU's secure/DCC page blocks framing (X-Frame-Options) so the popup shows
  // blank. Use the hosted redirect for non-INR: it loads full-page (no iframe)
  // and bills the foreign currency correctly. INR keeps the in-page popup.
  const currency = String((params.currency as string) || "INR").toUpperCase();

  // APPLE PAY routing: Apple Pay can only ever render on PayU's own HOSTED page
  // for us. Inside the Bolt overlay (an iframe on OUR domain) Apple requires the
  // merchant domain to serve PayU's domain-association file — and our
  // /.well-known path is Razorpay's (their Apple Pay depends on it, do not
  // touch). PayU docs: "File hosting is required on websites where PayU
  // Checkout loads as an overlay/iframe"; the full-page checkout has no such
  // requirement. ApplePaySession only exists in Apple-Pay-capable browsers
  // (Safari / WebKit with Wallet), so exactly the users who could see the
  // button get the hosted page where it can actually appear — everyone else
  // keeps the popup. This covers INR and non-INR alike.
  const canApplePay = (() => {
    try {
      return !!(window as any).ApplePaySession?.canMakePayments?.();
    } catch {
      return false;
    }
  })();

  if (currency !== "INR" || canApplePay) {
    submitRedirectForm(resp.payu_url, params);
    return;
  }

  try {
    await loadBolt(mode);
    const bolt = (window as any).bolt;
    if (!bolt?.launch) { submitRedirectForm(resp.payu_url, params); return; }

    bolt.launch(payuOnlyParams(params), {
      responseHandler: (B: any) => {
        if (settled) return;
        settled = true;
        const st = String(
          B?.response?.txnStatus ?? B?.response?.status ?? B?.txnStatus ?? B?.status ?? "",
        ).toLowerCase();
        onPopupResult(st === "success" || st === "captured" ? "success" : "failure");
      },
      catchException: () => {
        if (settled) return;
        settled = true;
        onPopupResult("failure");
      },
    });

    // If the overlay doesn't actually appear, fall back to the hosted redirect.
    const mounted = await waitForBoltFrame(2500);
    if (!mounted && !settled) {
      settled = true;
      submitRedirectForm(resp.payu_url, params);
    }
  } catch {
    if (!settled) submitRedirectForm(resp.payu_url, params);
  }
}
